import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { resource } from "@util/resource";
import { DWS } from "@interfaces/dws";
import { ConditionType, Matcher } from "interfaces/dws/matcher";
import { formatISO } from "date-fns";
import { ExportContact } from "../../interfaces/dws/contact";
import FilterContactMatcherValue = Matcher.FilterContactMatcherValue;
import * as moment from "moment";
import { map } from "rxjs/operators";
import { reject } from "lodash";

@Injectable({
  providedIn: "root",
})
export class ContactsService {
  constructor() {}

  filterBy(
    filter: DWS.Contact.Filter & {
      wineryId?: string;
      includeUnsubscribed?: boolean;
      includeSubscribed?: boolean;
      isArchived?: boolean;
    },
    page: number = 0,
    count: number = 999999,
    orderBy: string = "name",
    orderDir: string = "asc"
  ): Observable<DWS.Page<DWS.Contact>> {
    const creationStart = filter.wineryCreatedAt?.start ? moment(filter.wineryCreatedAt?.start).toDate() : undefined;
    const creationEnd = filter.wineryCreatedAt?.end ? moment(filter.wineryCreatedAt?.end).toDate() : undefined;
    const presenceStart = filter.presence?.start ? moment(filter.presence?.start).toDate() : undefined;
    const presenceEnd = filter.presence?.end ? moment(filter.presence?.end).toDate() : undefined;

    const queryFilters = {
      name: filter.name,
      email: filter.email,
      phone: filter.phone,
      barSearch: filter.barSearch,
      languageId: (filter.languages || []).map((l) => l.id),
      tagId: (filter.tag || []).map((t) => t.id),
      segmentId: (filter.segment || []).map((s) => s.id),
      typologyId: (filter.typology || []).map((t) => t.id),
      acceptedMarketing: (filter.privacies || []).some((f) => f.privacy == "acceptedMarketing") || undefined,
      acceptedPrivacyTerms: (filter.privacies || []).some((f) => f.privacy == "acceptedPrivacyTerms") || undefined,
      acceptedProfiling: (filter.privacies || []).some((f) => f.privacy == "acceptedProfiling") || undefined,
      manualMarketing: (filter.privacies || []).some((f) => f.privacy == "manualMarketing") || undefined,
      rejectedMarketing: (filter.privacies || []).some((f) => f.privacy == "rejectedMarketing") || undefined,
      customOriginId: (filter.origin || []).map((o) => o.id).filter((o) => typeof o === "string"),
      originId: (filter.origin || []).map((o) => o.id).map((o) => (typeof o !== "string" ? o : 20)),
      creationStart: creationStart ? formatISO(creationStart, { representation: "date" }) : undefined,
      creationEnd: creationEnd ? formatISO(creationEnd, { representation: "date" }) : undefined,
      presenceStart: presenceStart ? formatISO(presenceStart, { representation: "date" }) : undefined,
      presenceEnd: presenceEnd ? formatISO(presenceEnd, { representation: "date" }) : undefined,
      presenceMatch: ((filter.presence as any) || {}).presence,
      wineryId: filter.wineryId,
      isArchived: filter.isArchived || false,
      countryOfResidenceIso: (filter.countryOfResidenceIso && Array.isArray(filter.countryOfResidenceIso) && filter.countryOfResidenceIso.length > 0) 
        ? filter.countryOfResidenceIso.join(',')
        : undefined,
    };

    //console.log(queryFilters);
    return resource("crm://contacts/filter-by")
      .params({ page, count, ...queryFilters, orderBy, orderDir })
      .get<DWS.Page<DWS.Contact>>();
  }

  list(filter?: ContactsFilter, page: number = 0, count: number = 999999): Observable<DWS.Page<DWS.Contact>> {
    return resource("crm://contacts")
      .params({ page, count, ...filter })
      .get<DWS.Page<DWS.Contact>>();
  }

  listByMatcher(filter: FilterContactMatcherValue, page: number = 0, count: number = 999999): Observable<DWS.Page<DWS.FullContact>> {
    return resource("crm://contacts/matchers").params({ page, count }).post<DWS.Page<DWS.FullContact>>(filter);
  }

  get(contactId: number, companyId: string): Observable<DWS.Contact> {
    return resource("crm://contacts/id/companyId").params({ id: contactId, companyId: companyId }).get<DWS.Contact>();
  }

  getv2(contactId: number, companyId: string): Observable<DWS.Contact> {
    return resource("crm://contacts/v2/id/companyId")
      .params({ id: contactId, companyId: companyId })
      .get<any>()
      .pipe(
        map((response: any) => {
          const contactData = response; // Change this according to your API response structure
          const wineryId = contactData.winery?.id; // Use optional chaining to avoid errors
          contactData.wineryId = wineryId;
          return contactData as DWS.Contact; // Cast to DWS.Contact
        })
      );
  }

  getByEmail(email: string): Observable<DWS.Contact> {
    return resource("crm://contacts/email").params({ email, wineryId: "" }).post<DWS.Contact>();
  }

  ref(contactRef: string): Observable<DWS.Contact> {
    return resource("crm://contacts/ref").params({ ref: contactRef }).get<DWS.Contact>();
  }

  update(contactId: number | undefined, companyId: string, contact: DWS.Contact) {
    return resource("crm://contacts/id/companyId")
      .params({ id: contactId, companyId: companyId })
      .put({ ...contact, id: contactId });
  }

  updateShort(contactId: number | undefined, contact: DWS.Reservation.ReservationContactDTO) {
    return resource("crm://contacts/update-short/id")
      .params({ id: contactId })
      .put({ ...contact, id: contactId });
  }

  create(completeInput: DWS.ContactCompleteInput): Observable<any> {
    return resource("crm://contacts").post(completeInput);
  }

  createV2(contactData: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    contactType: string;
    countryISO: string;
    acceptedPrivacyTerms: boolean;
    acceptedMarketing: boolean;
    acceptedProfiling: boolean;
    wineryID: string;
    userSystemData?: {
      ipAddress?: string | null;
      origin?: string | null;
      userAgent?: string | null;
      deducedOrigin?: {
        id: number;
        nameIt?: string | null;
        nameEn?: string | null;
        shortNameIt?: string | null;
        shortNameEn?: string | null;
      };
    };
  }): Observable<any> {
    // Map frontend field names to backend expected field names
    const mappedData: any = {
      firstName: contactData.firstName,
      lastName: contactData.lastName,
      email: contactData.email,
      phoneNumber: contactData.phoneNumber,
      contactType: contactData.contactType,
      countryISO: contactData.countryISO,
      acceptedPrivacyTerms: contactData.acceptedPrivacyTerms,
      acceptedMarketing: contactData.acceptedMarketing,
      acceptedProfiling: contactData.acceptedProfiling,
      wineryID: contactData.wineryID
    };
    
    // Add userSystemData if provided - include all valid fields
    if (contactData.userSystemData) {
      mappedData.userSystemData = {
        ipAddress: contactData.userSystemData.ipAddress || null,
        origin: contactData.userSystemData.origin || null,
        userAgent: contactData.userSystemData.userAgent || null,
        deducedOrigin: contactData.userSystemData.deducedOrigin ? {
          id: contactData.userSystemData.deducedOrigin.id,
          nameIt: contactData.userSystemData.deducedOrigin.nameIt || null,
          nameEn: contactData.userSystemData.deducedOrigin.nameEn || null,
          shortNameIt: contactData.userSystemData.deducedOrigin.shortNameIt || null,
          shortNameEn: contactData.userSystemData.deducedOrigin.shortNameEn || null
        } : null
      };
    }
    
    return resource("crm://v2/contacts").post(mappedData);
  }

  contactsToExport(
    wineryId: string,
    creationStart: Date | undefined,
    creationEnd: Date | undefined,
    filter: DWS.Contact.Filter & { wineryId?: string; includeUnsubscribed?: boolean; includeSubscribed?: boolean; isArchived?: boolean }
  ): Observable<ExportContact[]> {
    const queryFilters = {
      name: filter.name,
      email: filter.email,
      phone: filter.phone,
      language: (filter.languages || []).map((l: any) => l.nameEn),
      tags: (filter.tag || []).map((t) => t.id),
      segment: (filter.segment || []).map((s) => s.name),
      typologyId: filter.typology || [1],
      acceptedMarketing: (filter.privacies || []).some((f) => f.privacy == "acceptedMarketing") || undefined,
      acceptedPrivacyTerms: (filter.privacies || []).some((f) => f.privacy == "acceptedPrivacyTerms") || undefined,
      acceptedProfiling: (filter.privacies || []).some((f) => f.privacy == "acceptedProfiling") || undefined,
      manualMarketing: (filter.privacies || []).some((f) => f.privacy == "manualMarketing") || undefined,
      rejectedMarketing: (filter.privacies || []).some((f) => f.privacy == "rejectedMarketing") || undefined,
      originId: (filter.origin || []).map((o) => o.id).map((o) => (typeof o !== "string" ? o : 20)),
      customOriginId: (filter.origin || []).map((o) => o.id).filter((o) => typeof o === "string"),
      presenceStart: filter.presence?.start ? formatISO(moment(filter.presence.start).toDate(), { representation: "date" }) : undefined,
      presenceEnd: filter.presence?.end ? formatISO(moment(filter.presence.end).toDate(), { representation: "date" }) : undefined,
      presenceMatch: ((filter.presence as any) || {}).presence,
      isArchived: filter.isArchived || false,
    };

    return resource("crm://contacts/export")
      .params({
        wineryId,
        creationStart: creationStart ? formatISO(creationStart, { representation: "date" }) : undefined,
        creationEnd: creationEnd ? formatISO(creationEnd, { representation: "date" }) : undefined,
        ...queryFilters,
      })
      .get<ExportContact[]>();
  }

  duplicatesByEmail(email: string): Observable<ContactDuplicatesDTO[]> {
    return resource("crm://v2/contacts/duplicates-by-email")
      .params({ email })
      .post<ContactDuplicatesDTO[]>(email);
  }

  mergeContacts(contactIdMaster: number, contactIdSlaves: number[]) {
    return resource("crm://v2/contacts/merge/contactIdMaster")
      .params({ contactIdMaster })
      .post(contactIdSlaves);
  }

  mergeAllContacts(email: string | null): Observable<MergeAllResult> {
    return resource("crm://v2/contacts/merge-all")
      .post(email);
  }
}

export interface ContactDuplicatesDTO {
  wineryId: string;
  contactId: number;
  isCompany: boolean;
  email: string;
  updatedAt: string;
  createdAt: string;
  originExternalId: string;
  deducedOriginId: number;
  deletedAt: string;
  acceptedMarketing: boolean;
  acceptedPrivacyTerms: boolean;
  acceptedProfiling: boolean;
  manualMarketing: boolean;
  informedOriginId: number;
  unsubscribed: boolean;
  selectedAsMaster: boolean;
}

export interface ContactsFilter {
  wineryIds: string[];
  includeUnsubscribed?: boolean | false;
  includeSubscribed?: boolean | false;
  tagId?: any[];
  audienceId?: any[];
  segmentId?: any[];
  q?: string;
  isArchived?: boolean;
  tourOperators?: boolean;
}

export interface ContactsFilterMatcher {
  wineryId: string;
  conditionType: ConditionType;
  values: ConditionType;
}

export interface MergeAllResult {
  success: boolean;
  message: string;
  totalMergedGroupsContacts: number;
  mergedContacts: MergedContact[];
}

export interface MergedContact {
  email: string;
  mergedContacts: number;
  contactIds: number[];
}